/**
 * The tenant's do-not-call list (plan E05-T05): look a number up, add or remove one, and import or
 * export the list as CSV. A number on it is never dialled; callers who opt out on a call land here.
 */
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useDenied } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useFormat, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState, QueryView } from "../ui/states";
import { Table } from "../ui/Table";
import { CSV } from "./files";
import { ImportExport } from "./ImportExport";

type Entry = Schemas["SuppressedOut"];
const E164 = /^\+[1-9]\d{6,14}$/;
const field = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

export function DoNotCall() {
  const t = useT();
  const format = useFormat();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const denied = useDenied();
  const canWrite = useCan("compliance:write");
  const queries = useQueryClient();
  const id = useId();
  const [lookup, setLookup] = useState("");
  const [number, setNumber] = useState<string | null>(null);
  const [adding, setAdding] = useState({ e164: "", reason: "" });
  const lookupProblem = lookup && !E164.test(lookup) ? t("compliance.e164") : null;
  const addProblem = adding.e164 && !E164.test(adding.e164) ? t("compliance.e164") : null;

  const list = useInfiniteQuery({
    queryKey: ["suppression", tenant, number],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      data(api.GET("/v1/suppression", { params: { query: { limit: 50, cursor: pageParam, e164: number ?? undefined } } })),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const refresh = () => queries.invalidateQueries({ queryKey: ["suppression", tenant] });
  const add = useMutation({
    mutationFn: () => data(api.POST("/v1/suppression", { body: { e164: adding.e164, reason: adding.reason || null } })),
    onSuccess: async () => {
      setAdding({ e164: "", reason: "" });
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (e: Entry) => api.DELETE("/v1/suppression/{e164}", { params: { path: { e164: e.e164 } } }),
    onSuccess: refresh,
  });

  return (
    <div className="space-y-4">
      <form role="search" aria-label={t("compliance.dnc.search")} className="flex flex-wrap items-end gap-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!lookupProblem) setNumber(lookup || null);
        }}>
        <div className="space-y-1">
          <label htmlFor={`${id}-lookup`} className="block text-sm font-medium">{t("compliance.number")}</label>
          <input id={`${id}-lookup`} className={`${field} font-mono`} value={lookup} placeholder="+919876543210"
            aria-invalid={lookupProblem ? true : undefined} aria-describedby={lookupProblem ? `${id}-lookup-error` : undefined}
            onChange={(e) => setLookup(e.target.value.trim())} />
          {lookupProblem && <p id={`${id}-lookup-error`} className="text-xs text-[var(--color-danger)]">{lookupProblem}</p>}
        </div>
        <Button type="submit" variant="secondary">{t("compliance.find")}</Button>
      </form>
      {canWrite && (
        <form aria-label={t("compliance.dnc.add")} className="flex flex-wrap items-end gap-3 rounded-lg border border-[var(--color-border)] p-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (adding.e164 && !addProblem) add.mutate();
          }}>
          <div className="space-y-1">
            <label htmlFor={`${id}-add`} className="block text-sm font-medium">{t("compliance.dnc.number")}</label>
            <input id={`${id}-add`} className={`${field} font-mono`} value={adding.e164} placeholder="+919876543210"
              aria-invalid={addProblem ? true : undefined} aria-describedby={addProblem ? `${id}-add-error` : undefined}
              onChange={(e) => setAdding({ ...adding, e164: e.target.value.trim() })} />
            {addProblem && <p id={`${id}-add-error`} className="text-xs text-[var(--color-danger)]">{addProblem}</p>}
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-reason`} className="block text-sm font-medium">{t("compliance.dnc.reason")}</label>
            <input id={`${id}-reason`} className={field} value={adding.reason} maxLength={500}
              onChange={(e) => setAdding({ ...adding, reason: e.target.value })} />
          </div>
          <Button type="submit" disabled={!adding.e164 || Boolean(addProblem) || add.isPending}>{t("compliance.dnc.addButton")}</Button>
        </form>
      )}
      {add.isError && <ErrorState error={add.error} />}
      {remove.isError && <ErrorState error={remove.error} />}
      <ImportExport
        name="do-not-call"
        canImport={canWrite}
        send={async (csv) => {
          const r = await data(api.POST("/v1/suppression/import", { body: csv, ...CSV }));
          await refresh();
          return { done: t("compliance.dnc.imported", { added: r.added }), errors: r.errors };
        }}
        fetchCsv={async () => (await data(api.GET("/v1/suppression/export", { parseAs: "text" }))) as unknown as string}
      />
      <QueryView query={list} denied={denied}>
        {(pages) => {
          const rows = pages.pages.flatMap((p) => p.data);
          if (rows.length === 0) {
            return (
              <Empty title={number ? t("compliance.dnc.notListed", { number }) : t("compliance.dnc.empty")}>
                <p>{t("compliance.dnc.emptyBody")}</p>
              </Empty>
            );
          }
          return (
            <div className="space-y-3">
              <Table
                caption={t("compliance.tab.dnc")}
                rows={rows}
                rowKey={(e) => e.e164}
                columns={[
                  { header: t("compliance.number"), cell: (e) => <span className="font-mono">{e.e164}</span> },
                  { header: t("compliance.dnc.source"), cell: (e) => t(`compliance.dnc.from.${e.source}`) },
                  { header: t("compliance.dnc.reason"), cell: (e) => e.reason ?? "—" },
                  { header: t("compliance.dnc.added"), cell: (e) => format.dateTime(e.created_at) },
                  {
                    header: t("compliance.dnc.until"),
                    cell: (e) => (e.expires_at ? format.dateTime(e.expires_at) : t("compliance.dnc.forever")),
                  },
                  ...(canWrite
                    ? [{
                        header: t("compliance.consents.actions"),
                        cell: (e: Entry) => (
                          <Button variant="ghost" disabled={remove.isPending}
                            aria-label={t("compliance.dnc.removeFor", { number: e.e164 })}
                            onClick={() => {
                              if (window.confirm(t("compliance.dnc.removeConfirm", { number: e.e164 }))) remove.mutate(e);
                            }}>
                            {t("compliance.dnc.remove")}
                          </Button>
                        ),
                      }]
                    : []),
                ]}
              />
              {list.hasNextPage && (
                <Button variant="secondary" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                  {t("compliance.more")}
                </Button>
              )}
            </div>
          );
        }}
      </QueryView>
    </div>
  );
}
